export interface Link {
	url?: string;
}

export interface Feature {
	/** @format uuid */
	id?: string;
	name?: string;
	description?: string;
	enabled?: boolean;
	links?: Record<string, Link>;
}

export interface Journey {
	/** @format uuid */
	id?: string;
	name?: string;
	description?: string;
	links?: Record<string, Link>;
}

export type QueryParamsType = Record<string | number, any>;
export type ResponseFormat = keyof Omit<Body, 'body' | 'bodyUsed'>;

export interface FullRequestParams extends Omit<RequestInit, 'body'> {
	/** set parameter to `true` for call `securityWorker` for this request */
	secure?: boolean;
	/** request path */
	path: string;
	/** content type of request body */
	type?: ContentType;
	/** query params */
	query?: QueryParamsType;
	/** format of response (i.e. response.json() -> format: "json") */
	format?: ResponseFormat;
	/** request body */
	body?: unknown;
	/** base url */
	baseUrl?: string;
	/** request cancellation token */
	cancelToken?: CancelToken;
}

export type RequestParams = Omit<FullRequestParams, 'body' | 'method' | 'query' | 'path'>;

export interface ApiConfig<SecurityDataType = unknown> {
	baseUrl?: string;
	baseApiParams?: Omit<RequestParams, 'baseUrl' | 'cancelToken' | 'signal'>;
	securityWorker?: (securityData: SecurityDataType | null) => Promise<RequestParams | void> | RequestParams | void;
	customFetch?: typeof fetch;
}

export interface HttpResponse<D extends unknown, E extends unknown = unknown> extends Response {
	data: D;
	error: E;
}

type CancelToken = symbol | string | number;

export enum ContentType {
	Json = 'application/json',
	FormData = 'multipart/form-data',
	UrlEncoded = 'application/x-www-form-urlencoded',
}

export class HttpClient<SecurityDataType = unknown> {
	public baseUrl = '';
	private securityData: SecurityDataType | null = null;
	private securityWorker?: ApiConfig<SecurityDataType>['securityWorker'];
	private abortControllers = new Map<CancelToken, AbortController>();
	private customFetch = (...fetchParams: Parameters<typeof fetch>) => fetch(...fetchParams);

	private baseApiParams: RequestParams = {
		credentials: 'same-origin',
		headers: {},
		redirect: 'follow',
		referrerPolicy: 'no-referrer',
	};

	constructor(apiConfig: ApiConfig<SecurityDataType> = {}) {
		Object.assign(this, apiConfig);
	}

	public setSecurityData = (data: SecurityDataType | null) => {
		this.securityData = data;
	};

	protected encodeQueryParam(key: string, value: any) {
		const encodedKey = encodeURIComponent(key);
		return `${encodedKey}=${encodeURIComponent(typeof value === 'number' ? value : `${value}`)}`;
	}

	protected addQueryParam(query: QueryParamsType, key: string) {
		return this.encodeQueryParam(key, query[key]);
	}

	protected addArrayQueryParam(query: QueryParamsType, key: string) {
		const value = query[key];
		return value.map((v: any) => this.encodeQueryParam(key, v)).join('&');
	}

	protected toQueryString(rawQuery?: QueryParamsType): string {
		const query = rawQuery || {};
		const keys = Object.keys(query).filter((key) => typeof query[key] !== 'undefined');
		return keys
			.map((key) => (Array.isArray(query[key]) ? this.addArrayQueryParam(query, key) : this.addQueryParam(query, key)))
			.join('&');
	}

	protected addQueryParams(rawQuery?: QueryParamsType): string {
		const queryString = this.toQueryString(rawQuery);
		return queryString ? `?${queryString}` : '';
	}

	private contentFormatters: Record<ContentType, (input: any) => any> = {
		[ContentType.Json]: (input: any) =>
			input !== null && (typeof input === 'object' || typeof input === 'string') ? JSON.stringify(input) : input,
		[ContentType.FormData]: (input: any) =>
			Object.keys(input || {}).reduce((formData, key) => {
				const property = input[key];
				formData.append(
					key,
					property instanceof Blob
						? property
						: typeof property === 'object' && property !== null
							? JSON.stringify(property)
							: `${property}`,
				);
				return formData;
			}, new FormData()),
		[ContentType.UrlEncoded]: (input: any) => this.toQueryString(input),
	};

	protected mergeRequestParams(params1: RequestParams, params2?: RequestParams): RequestParams {
		return {
			...this.baseApiParams,
			...params1,
			...(params2 || {}),
			headers: {
				...(this.baseApiParams.headers || {}),
				...(params1.headers || {}),
				...((params2 && params2.headers) || {}),
			},
		};
	}

	protected createAbortSignal = (cancelToken: CancelToken): AbortSignal | undefined => {
		if (this.abortControllers.has(cancelToken)) {
			const abortController = this.abortControllers.get(cancelToken);
			if (abortController) {
				return abortController.signal;
			}
			return void 0;
		}

		const abortController = new AbortController();
		this.abortControllers.set(cancelToken, abortController);
		return abortController.signal;
	};

	public abortRequest = (cancelToken: CancelToken) => {
		const abortController = this.abortControllers.get(cancelToken);

		if (abortController) {
			abortController.abort();
			this.abortControllers.delete(cancelToken);
		}
	};

	public request = async <T = any, E = any>({
		body,
		secure,
		path,
		type,
		query,
		format,
		baseUrl,
		cancelToken,
		...params
	}: FullRequestParams): Promise<T> => {
		const secureParams =
			((typeof secure === 'boolean' ? secure : this.baseApiParams.secure) &&
				this.securityWorker &&
				(await this.securityWorker(this.securityData))) ||
			{};
		const requestParams = this.mergeRequestParams(params, secureParams);
		const queryString = query && this.toQueryString(query);
		const payloadFormatter = this.contentFormatters[type || ContentType.Json];
		const responseFormat = format || requestParams.format;

		return this.customFetch(`${baseUrl || this.baseUrl || ''}${path}${queryString ? `?${queryString}` : ''}`, {
			...requestParams,
			headers: {
				...(requestParams.headers || {}),
				...(type && type !== ContentType.FormData ? { 'Content-Type': type, } : {}),
			},
			signal: cancelToken ? this.createAbortSignal(cancelToken) : requestParams.signal,
			body: typeof body === 'undefined' || body === null ? null : payloadFormatter(body),
		}).then(async (response) => {
			const r = response as HttpResponse<T, E>;
			r.data = null as unknown as T;
			r.error = null as unknown as E;

			const data = !responseFormat
				? r
				: await response[responseFormat]()
					.then((data) => {
						if (r.ok) {
							r.data = data;
						} else {
							r.error = data;
						}
						return r;
					})
					.catch((e) => {
						r.error = e;
						return r;
					});

			if (cancelToken) {
				this.abortControllers.delete(cancelToken);
			}

			if (!response.ok) {
				throw data;
			}
			return data.data;
		});
	};
}

/**
 * @title YourAccount.Features
 * @version 1.0
 */
export class Api<SecurityDataType extends unknown> extends HttpClient<SecurityDataType> {
	api = {
		/**
		 * No description
		 *
		 * @tags Feature
		 * @name JourneyFeatureDetail
		 * @request GET:/api/Journey/{journeyId}/Feature
		 * @response `200` `(Feature)[]` Success
		 */
		journeyFeatureDetail: (journeyId: string, params: RequestParams = {}) =>
			this.request<Feature[], any>({
				path: `/api/Journey/${journeyId}/Feature`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Feature
		 * @name JourneyFeatureCreate
		 * @request POST:/api/Journey/{journeyId}/Feature
		 * @response `200` `Journey` Success
		 */
		journeyFeatureCreate: (journeyId: string, data: Journey, params: RequestParams = {}) =>
			this.request<Journey, any>({
				path: `/api/Journey/${journeyId}/Feature`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Feature
		 * @name JourneyFeatureDetail2
		 * @request GET:/api/Journey/{journeyId}/Feature/{id}
		 * @originalName journeyFeatureDetail
		 * @duplicate
		 * @response `200` `Journey` Success
		 */
		journeyFeatureDetail2: (
			journeyId: string,
			id: string,
			query?: {
				/** @format uuid */
				featureId?: string,
			},
			params: RequestParams = {},
		) =>
			this.request<Journey, any>({
				path: `/api/Journey/${journeyId}/Feature/${id}`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Feature
		 * @name JourneyFeatureUpdate
		 * @request PUT:/api/Journey/{journeyId}/Feature/{id}
		 * @response `200` `Journey` Success
		 */
		journeyFeatureUpdate: (journeyId: string, id: string, data: Journey, params: RequestParams = {}) =>
			this.request<Journey, any>({
				path: `/api/Journey/${journeyId}/Feature/${id}`,
				method: 'PUT',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Feature
		 * @name JourneyFeatureDelete
		 * @request DELETE:/api/Journey/{journeyId}/Feature/{id}
		 * @response `204` `void` No Content
		 */
		journeyFeatureDelete: (journeyId: string, id: string, params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/Journey/${journeyId}/Feature/${id}`,
				method: 'DELETE',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Journey
		 * @name JourneyList
		 * @request GET:/api/Journey
		 * @response `200` `(Journey)[]` Success
		 */
		journeyList: (params: RequestParams = {}) =>
			this.request<Journey[], any>({
				path: `/api/Journey`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Journey
		 * @name JourneyCreate
		 * @request POST:/api/Journey
		 * @response `200` `Journey` Success
		 */
		journeyCreate: (data: Journey, params: RequestParams = {}) =>
			this.request<Journey, any>({
				path: `/api/Journey`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Journey
		 * @name JourneyDetail
		 * @request GET:/api/Journey/{id}
		 * @response `200` `Journey` Success
		 */
		journeyDetail: (id: string, params: RequestParams = {}) =>
			this.request<Journey, any>({
				path: `/api/Journey/${id}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Journey
		 * @name JourneyUpdate
		 * @request PUT:/api/Journey/{id}
		 * @response `200` `Journey` Success
		 */
		journeyUpdate: (id: string, data: Journey, params: RequestParams = {}) =>
			this.request<Journey, any>({
				path: `/api/Journey/${id}`,
				method: 'PUT',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Journey
		 * @name JourneyDelete
		 * @request DELETE:/api/Journey/{id}
		 * @response `204` `void` No Content
		 */
		journeyDelete: (id: string, params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/Journey/${id}`,
				method: 'DELETE',
				...params,
			}),
	};
}
